import { differenceInDays, formatDistanceToNowStrict, fromUnixTime } from 'date-fns';
import plur from 'plur';
import { computed } from 'nanostores';
import { accountStore } from '@/services/auth/store';
import { AccountState, PlanFeature } from '@/services/auth/types';
import { billing } from '@/services/billing';
import { isEmptyString } from '@/utils/string';

export const shouldShowUpgradeCta = computed(
  [accountStore, billing.futureSubscription, billing.isOrdwayCustomer, billing.isTrial],
  (account, future, isOrdway, trial) => {
    if (account?.account_state === AccountState.Canceled) {
      return false;
    }

    if (future != null) {
      return false;
    }

    if (isOrdway) {
      return trial;
    }

    return account?.suspend_trial_at != null || account?.planFeatures?.[PlanFeature.Messaging]?.expiresAt != null;
  },
);

const TRIAL_EXPIRING_SOON_IN_DAYS = 8;
const TRIAL_EXPIRING_IMMINENTLY_IN_DAYS = 3;

export type TrialStatus = 'critical' | 'warning' | 'success';

/** Ports the Ember `app-header/trial-progress` component's `trialStatus` getter. */
export function getTrialStatus(daysLeftInTrial: number): TrialStatus {
  if (daysLeftInTrial < TRIAL_EXPIRING_IMMINENTLY_IN_DAYS) {
    return 'critical';
  }
  if (daysLeftInTrial < TRIAL_EXPIRING_SOON_IN_DAYS) {
    return 'warning';
  }
  return 'success';
}

/**
 * Ports the Ember `trial-indicator` component's `finalFormatter`. Ordway trial
 * dates are day-granularity, so they're formatted as a whole day count; other
 * accounts get a magnitude-only relative format (no "in"/"ago" prefix), matching
 * Ember's `moment(...).fromNow(true)`. Returns `''` when there's no trial end
 * date to format.
 */
export function formatTrialTimeLeft(trialExpiresAt: number | null, isOrdwayCustomer: boolean): string {
  if (trialExpiresAt == null) {
    return '';
  }

  const expiresAtDate = fromUnixTime(trialExpiresAt);

  if (isOrdwayCustomer) {
    const daysLeft = differenceInDays(expiresAtDate, new Date());
    return `${daysLeft} ${plur('day', daysLeft)}`;
  }

  return formatDistanceToNowStrict(expiresAtDate);
}

function getExpiredTrialDescription(isPendingCheckout: boolean, hasBillingViewPermission: boolean): string {
  if (isPendingCheckout) {
    return '';
  }

  return hasBillingViewPermission
    ? 'Your trial has ended'
    : 'You cannot access your data until the account is upgraded.';
}

function getTrialEndingDescription(daysLeftInTrial: number, isPendingCheckout: boolean): string {
  const base = daysLeftInTrial === 0 ? 'Your trial ends today' : 'Your trial has ended';
  return isPendingCheckout ? `${base} and will automatically upgrade` : base;
}

function getUpcomingExpiryDescription(formattedTimeLeft: string, isPendingCheckout: boolean): string {
  return isPendingCheckout
    ? `Your trial will automatically upgrade in ${formattedTimeLeft}`
    : `${formattedTimeLeft} left in trial`;
}

function getUnknownExpiryDescription(isPendingCheckout: boolean): string {
  return isPendingCheckout ? 'Your trial will automatically upgrade soon' : 'Your trial will end soon';
}

type TrialExpiryDescriptionArgs = {
  daysLeftInTrial: number;
  formattedTimeLeft: string;
  hasBillingViewPermission: boolean;
  isPendingCheckout: boolean;
  trialExpired: boolean;
  trialExpiresAt: number | null;
};

/** Ports the Ember `trial-expiry-description` component's template logic. */
export function getTrialExpiryDescription({
  daysLeftInTrial,
  formattedTimeLeft,
  hasBillingViewPermission,
  isPendingCheckout,
  trialExpired,
  trialExpiresAt,
}: TrialExpiryDescriptionArgs): string {
  if (trialExpired) {
    return getExpiredTrialDescription(isPendingCheckout, hasBillingViewPermission);
  }

  if (daysLeftInTrial < 1) {
    return getTrialEndingDescription(daysLeftInTrial, isPendingCheckout);
  }

  if (trialExpiresAt != null) {
    return getUpcomingExpiryDescription(formattedTimeLeft, isPendingCheckout);
  }

  return getUnknownExpiryDescription(isPendingCheckout);
}

function getShouldShowUpgradeButton(
  isSampleWorkspace: boolean,
  isPendingCheckout: boolean,
  hasBillingViewPermission: boolean,
): boolean {
  return !isSampleWorkspace && !isPendingCheckout && hasBillingViewPermission;
}

function getTooltipContent(isUpgradeDisabled: boolean, upgradeDisabledMessage: string): string {
  return isUpgradeDisabled ? upgradeDisabledMessage : 'Upgrade';
}

function getIsTooltipDisabled(
  isUpgradeDisabled: boolean,
  upgradeDisabledMessage: string,
  isCollapsed: boolean,
): boolean {
  return isUpgradeDisabled ? isEmptyString(upgradeDisabledMessage) : !isCollapsed;
}

function getUpgradeHref(hasPinnedPlan: boolean): string {
  return hasPinnedPlan ? '/settings/billing/checkout' : '/settings/billing/available-plans/journeys';
}

export type TrialCtaDisplayInput = {
  daysLeftInTrial: number;
  hasBillingViewPermission: boolean;
  hasPinnedPlan: boolean;
  isCollapsed: boolean;
  isOrdwayCustomer: boolean;
  isPendingCheckout: boolean;
  isSampleWorkspace: boolean;
  isUpgradeDisabled: boolean;
  trialExpired: boolean;
  trialExpiresAt: number | null;
  upgradeDisabledMessage: string;
};

export type TrialCtaDisplayData = {
  expiryDescription: string;
  isTooltipDisabled: boolean;
  isUpgradeDisabled: boolean;
  shouldShowUpgradeButton: boolean;
  tooltipContent: string;
  trialStatus: TrialStatus;
  upgradeHref: string;
};

/**
 * Pure derivation of everything `TrialCta` needs to render, given already-read
 * billing/account/nav state. Colocated as one plain function (rather than
 * inline in the `useTrialCtaData` hook) so it stays directly unit-testable
 * without rendering a component, and so the hook itself stays a thin wiring
 * layer between React/nanostores and this calculation.
 */
export function getTrialCtaDisplayData(input: TrialCtaDisplayInput): TrialCtaDisplayData {
  const formattedTimeLeft = formatTrialTimeLeft(input.trialExpiresAt, input.isOrdwayCustomer);

  return {
    expiryDescription: getTrialExpiryDescription({
      daysLeftInTrial: input.daysLeftInTrial,
      formattedTimeLeft,
      hasBillingViewPermission: input.hasBillingViewPermission,
      isPendingCheckout: input.isPendingCheckout,
      trialExpired: input.trialExpired,
      trialExpiresAt: input.trialExpiresAt,
    }),
    isTooltipDisabled: getIsTooltipDisabled(input.isUpgradeDisabled, input.upgradeDisabledMessage, input.isCollapsed),
    isUpgradeDisabled: input.isUpgradeDisabled,
    shouldShowUpgradeButton: getShouldShowUpgradeButton(
      input.isSampleWorkspace,
      input.isPendingCheckout,
      input.hasBillingViewPermission,
    ),
    tooltipContent: getTooltipContent(input.isUpgradeDisabled, input.upgradeDisabledMessage),
    trialStatus: getTrialStatus(input.daysLeftInTrial),
    upgradeHref: getUpgradeHref(input.hasPinnedPlan),
  };
}
